import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Alert, Box, Divider, Paper } from '@mui/material';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import type { AnyExtension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import {
    attachmentService,
    type AttachmentVO,
} from '@/services/auditService/attachment';
import { FileKeyImage } from '@/pages/components/common/richText/fileKeyImage';
import { RichTextToolbar } from '@/pages/components/common/richText/RichTextToolbar';
import { RichTextAttachmentPanel } from '@/pages/components/common/richText/RichTextAttachmentPanel';
import type { RichTextEditorProps } from '@/pages/components/common/richText/types';
import {
    canUpload,
    hasUnhydratedFileKeyImages,
    hydrateInlineImagesHtml,
    isImageFile,
    revokeObjectUrls,
    toPersistHtml,
    toUploadBo,
    uploadAndFindNew,
} from '@/pages/components/common/richText/htmlUtils';

export type {
    RichTextDocFormat,
    RichTextEditorProps,
    RichTextUploadContext,
} from '@/pages/components/common/richText/types';

function serializeEditor(editor: Editor, format: 'html' | 'json'): string {
    if (format === 'json') {
        return JSON.stringify(editor.getJSON());
    }
    return toPersistHtml(editor.getHTML());
}

function parseIncomingValue(value: string, format: 'html' | 'json'): string | Record<string, unknown> {
    if (format === 'json') {
        if (!value) return { type: 'doc', content: [{ type: 'paragraph' }] };
        try {
            return JSON.parse(value) as Record<string, unknown>;
        } catch {
            return { type: 'doc', content: [{ type: 'paragraph' }] };
        }
    }
    return value || '';
}

/**
 * Generic TipTap rich-text editor with inline image paste (MinIO) and a bottom Attachment panel.
 * See docs/rich-text-editor-design.md.
 */
export function RichTextEditor({
    value,
    onChange,
    format = 'html',
    readOnly = false,
    placeholder = 'Enter notes...',
    minHeight = 160,
    uploadContext,
    enableImage = true,
    enableAttachment = true,
    onAttachmentsChange,
    onUploadError,
}: RichTextEditorProps): React.ReactElement {
    const lastEmittedRef = useRef<string>('');
    const objectUrlsRef = useRef<string[]>([]);
    // Block onChange until first external hydrate finishes when value has file-key images.
    const applyingExternalRef = useRef(
        format === 'html' && hasUnhydratedFileKeyImages(value || ''),
    );
    const editorRef = useRef<Editor | null>(null);
    const uploadContextRef = useRef(uploadContext);
    uploadContextRef.current = uploadContext;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const formatRef = useRef(format);
    formatRef.current = format;
    const onUploadErrorRef = useRef(onUploadError);
    onUploadErrorRef.current = onUploadError;
    const readOnlyRef = useRef(readOnly);
    readOnlyRef.current = readOnly;
    const enableImageRef = useRef(enableImage);
    enableImageRef.current = enableImage;
    const enableAttachmentRef = useRef(enableAttachment);
    enableAttachmentRef.current = enableAttachment;

    const reportError = useCallback((message: string) => {
        onUploadErrorRef.current?.(message);
    }, []);

    const insertImageFiles = useCallback(async (ed: Editor, files: File[]) => {
        const images = files.filter(isImageFile);
        if (images.length === 0) return;
        const ctx = uploadContextRef.current;
        if (!canUpload(ctx)) {
            reportError('Missing upload context or target is not saved yet. Save the record before pasting images.');
            return;
        }
        try {
            const bo = toUploadBo(ctx, ctx.targetType);
            const created = await uploadAndFindNew(images, bo);
            for (let i = 0; i < created.length; i += 1) {
                const att = created[i];
                const file = images[i] ?? images[0];
                let src = '';
                try {
                    const blob = await attachmentService.fetchAttachmentBlob({
                        companyId: ctx.companyId,
                        annualId: ctx.annualId,
                        fileKey: att.fileKey,
                    });
                    src = URL.createObjectURL(blob);
                    objectUrlsRef.current.push(src);
                } catch {
                    src = URL.createObjectURL(file);
                    objectUrlsRef.current.push(src);
                }
                ed.chain().focus().insertContent({
                    type: 'image',
                    attrs: {
                        src,
                        alt: att.fileName || file.name,
                        fileKey: att.fileKey,
                        attachmentId: att.id,
                    },
                }).run();
            }
            if (created.length === 0) {
                reportError('Image uploaded but attachment metadata was not found. Please retry.');
            }
        } catch (e) {
            reportError(e instanceof Error ? e.message : 'Image upload failed');
        }
    }, [reportError]);

    const insertImageFilesRef = useRef(insertImageFiles);
    insertImageFilesRef.current = insertImageFiles;

    const extensions = useMemo((): AnyExtension[] => {
        const list: AnyExtension[] = [
            StarterKit,
            Placeholder.configure({ placeholder }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
            }),
        ];
        if (enableImage) {
            list.push(FileKeyImage.configure({ inline: false, allowBase64: false }));
        }
        return list;
    }, [placeholder, enableImage]);

    // Avoid mounting TipTap on unhydrated persist HTML (empty/missing src); hydrate effect fills content.
    const initialContent = format === 'html'
        ? (hasUnhydratedFileKeyImages(value || '') ? '' : (value || ''))
        : parseIncomingValue(value, 'json');

    const editor = useEditor({
        extensions,
        content: initialContent,
        editable: !readOnly,
        editorProps: {
            attributes: {
                class: 'rich-text-editor-content',
            },
            handlePaste: (_view, event) => {
                if (readOnlyRef.current || !enableImageRef.current) return false;
                const clipboard = event.clipboardData;
                if (!clipboard) return false;

                const imageFiles: File[] = [];
                for (let i = 0; i < clipboard.files.length; i += 1) {
                    const f = clipboard.files.item(i);
                    if (f && isImageFile(f)) imageFiles.push(f);
                }
                if (imageFiles.length === 0) {
                    for (let i = 0; i < clipboard.items.length; i += 1) {
                        const item = clipboard.items[i];
                        if (item.kind === 'file' && item.type.startsWith('image/')) {
                            const f = item.getAsFile();
                            if (f) imageFiles.push(f);
                        }
                    }
                }

                const ed = editorRef.current;
                if (imageFiles.length > 0 && ed) {
                    event.preventDefault();
                    void insertImageFilesRef.current(ed, imageFiles);
                    return true;
                }

                // Non-image file paste ? do not embed; use bottom Attachment panel
                if (clipboard.files.length > 0 && enableAttachmentRef.current) {
                    const hasNonImage = Array.from(clipboard.files).some((f) => !isImageFile(f));
                    if (hasNonImage) {
                        event.preventDefault();
                        reportError('Non-image files should be added via the Attachments panel below.');
                        return true;
                    }
                }
                return false;
            },
            handleDrop: (_view, event) => {
                if (readOnlyRef.current || !enableImageRef.current) return false;
                const dt = event.dataTransfer;
                if (!dt?.files?.length) return false;
                const images = Array.from(dt.files).filter(isImageFile);
                if (images.length === 0) return false;
                const ed = editorRef.current;
                if (!ed) return false;
                event.preventDefault();
                void insertImageFilesRef.current(ed, images);
                return true;
            },
        },
        onUpdate: ({ editor: ed }) => {
            if (applyingExternalRef.current) return;
            const next = serializeEditor(ed, formatRef.current);
            if (next === lastEmittedRef.current) return;
            lastEmittedRef.current = next;
            onChangeRef.current(next);
        },
    }, [extensions]);

    editorRef.current = editor;

    useEffect(() => {
        if (!editor) return;
        editor.setEditable(!readOnly);
    }, [editor, readOnly]);

    useEffect(() => {
        if (!editor) return;
        const incomingPersist = format === 'html'
            ? toPersistHtml(value || '')
            : (value || '');
        // Echo from our own onChange: persist HTML is unchanged.
        // Do NOT setContent here — even if images still look "unhydrated" in the
        // persist string (empty src + data-file-key). Re-applying HTML resets the
        // caret to the end (QI1 Attachment/Notes jump bug).
        if (incomingPersist === lastEmittedRef.current) {
            applyingExternalRef.current = false;
            return;
        }

        let cancelled = false;
        (async () => {
            applyingExternalRef.current = true;
            try {
                revokeObjectUrls(objectUrlsRef.current);
                objectUrlsRef.current = [];
                const ctx = uploadContextRef.current;

                if (format === 'json') {
                    const json = parseIncomingValue(value, 'json');
                    editor.commands.setContent(json, false);
                    const { state } = editor;
                    const updates: Array<{ pos: number; attrs: Record<string, unknown> }> = [];
                    const urls: string[] = [];
                    const tasks: Array<Promise<void>> = [];
                    state.doc.descendants((node, pos) => {
                        if (node.type.name !== 'image') return;
                        const fileKey = node.attrs.fileKey as string | null;
                        if (!fileKey) return;
                        const src = node.attrs.src as string | null;
                        if (src && (src.startsWith('blob:') || src.startsWith('http'))) return;
                        const snapshotAttrs = { ...node.attrs };
                        tasks.push((async () => {
                            try {
                                const blob = await attachmentService.fetchAttachmentBlob({
                                    companyId: ctx?.companyId,
                                    annualId: ctx?.annualId,
                                    fileKey,
                                });
                                const url = URL.createObjectURL(blob);
                                urls.push(url);
                                updates.push({ pos, attrs: { ...snapshotAttrs, src: url } });
                            } catch {
                                // ignore
                            }
                        })());
                    });
                    await Promise.all(tasks);
                    if (cancelled) {
                        revokeObjectUrls(urls);
                        return;
                    }
                    objectUrlsRef.current.push(...urls);
                    if (updates.length > 0) {
                        let tr = editor.state.tr;
                        updates.forEach((u) => {
                            tr = tr.setNodeMarkup(u.pos, undefined, u.attrs);
                        });
                        editor.view.dispatch(tr);
                    }
                } else {
                    const hydrated = await hydrateInlineImagesHtml(
                        value || '',
                        ctx,
                        objectUrlsRef.current,
                    );
                    if (cancelled) return;
                    // Hydrated HTML has blob: src so TipTap Image parseHTML keeps nodes.
                    editor.commands.setContent(hydrated || '', false);
                }
                lastEmittedRef.current = incomingPersist;
            } finally {
                if (!cancelled) {
                    applyingExternalRef.current = false;
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [editor, value, format, uploadContext?.companyId, uploadContext?.annualId]);

    useEffect(() => () => {
        revokeObjectUrls(objectUrlsRef.current);
        objectUrlsRef.current = [];
    }, []);

    const handleToolbarImages = useCallback((files: File[]) => {
        if (!editor) return;
        void insertImageFiles(editor, files);
    }, [editor, insertImageFiles]);

    const handleAttachmentsChange = useCallback((items: AttachmentVO[]) => {
        onAttachmentsChange?.(items);
    }, [onAttachmentsChange]);

    return (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            {!readOnly && (
                <>
                    <RichTextToolbar
                        editor={editor}
                        enableImage={enableImage}
                        onInsertImageFiles={handleToolbarImages}
                        disabled={!editor}
                    />
                    <Divider />
                </>
            )}

            {!canUpload(uploadContext) && enableImage && !readOnly && (
                <Alert severity="info" sx={{ borderRadius: 0, py: 0 }}>
                    Save the record first to enable image paste / upload.
                </Alert>
            )}

            <Box
                sx={{
                    minHeight,
                    px: 1.5,
                    py: 1,
                    '& .ProseMirror': {
                        outline: 'none',
                        minHeight: minHeight - 16,
                        fontSize: 14,
                        lineHeight: 1.5,
                        '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            display: 'block',
                            my: 1,
                        },
                        '& p.is-editor-empty:first-of-type::before': {
                            color: 'text.disabled',
                            content: 'attr(data-placeholder)',
                            float: 'left',
                            height: 0,
                            pointerEvents: 'none',
                        },
                    },
                }}
            >
                <EditorContent editor={editor} />
            </Box>

            {enableAttachment && (
                <>
                    <Divider />
                    <RichTextAttachmentPanel
                        uploadContext={uploadContext}
                        readOnly={readOnly}
                        onAttachmentsChange={handleAttachmentsChange}
                        onUploadError={onUploadError}
                    />
                </>
            )}
        </Paper>
    );
}

export default RichTextEditor;
